import { useUserContext } from "../../Hooks/useUserContext";
import { useVolumeToMassConversionService } from "../../Hooks/useVolumeToMassConversionService";
import { RouterLink } from "../../Shared/RouterLink";
import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	TextField,
	Button,
	Paper,
	Snackbar,
	SnackbarContent
} from "@material-ui/core";
import NotLoggedInIcon from "@material-ui/icons/MoodBad";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import ConfirmIcon from "@material-ui/icons/CheckCircle";
import { AutoResizeTextarea } from "../../Shared/AutoResizeTextarea";
import green from "@material-ui/core/colors/green";

export function EditVolumeToMassConversion(props) {
	const user = useUserContext(props.config);
	const service = useVolumeToMassConversionService(props.config);
	const isCreateMode = props.match.params.volumeToMassConversionId === undefined;
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [toastOpen, setToastOpen] = useState(false);
	const [item, setItem] = useState({
		volumeToMassConversionId: 0,
		name: "",
		density: 0,
		aliases: "",
		notes: ""
	});

	const hasPermission = user?.isVolumeToMassAdmin ?? false;

	const normalizeAliasesForEditor = (aliases) => {
		if (!aliases) return "";
		if (Array.isArray(aliases)) {
			return aliases
				.map((alias) => (alias ?? "").trim())
				.filter((alias) => alias)
				.join("\n");
		}
		return aliases
			.split(/\||\r?\n/)
			.map((alias) => alias.trim())
			.filter((alias) => alias)
			.join("\n");
	};

	const toAliasesArray = (aliasesText) => {
		if (!aliasesText) return [];
		return aliasesText
			.split(/\||\r?\n/)
			.map((alias) => alias.trim())
			.filter((alias) => alias);
	};

	useEffect(() => {
		const title = isCreateMode ? "Create Volume to Mass Conversion" : "Edit Volume to Mass Conversion";
		document.title = `${title} - ${props.config.appName}`;

		if (!hasPermission)
			return;

		if (isCreateMode) {
			setIsLoading(false);
		} else {
			const id = parseInt(props.match.params.volumeToMassConversionId);
			service.getById(id, (response) => {
				if (response && response.status === 200 && response.data) {
					setItem({
						...response.data,
						aliases: normalizeAliasesForEditor(response.data.aliases)
					});
				}
				setIsLoading(false);
			}, (error) => {
				console.error(error);
				if (error.response && error.response.status === 404) {
					props.history.push("/notfound");
				} else {
					alert("Failed to load volume to mass conversion");
				}
				setIsLoading(false);
			});
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function handleChange(event) {
		const { name, value } = event.target;
		setItem({
			...item,
			[name]: name === "density" ? parseFloat(value) || 0 : value
		});
	}

	function handleSave() {
		setIsSaving(true);

		const saveFunction = isCreateMode ? service.create : service.update;
		const payload = {
			VolumeToMassConversionId: item.volumeToMassConversionId,
			Name: item.name,
			Density: item.density,
			Aliases: toAliasesArray(item.aliases),
			Notes: item.notes
		};
		const saveArgs = isCreateMode ? [payload] : [item.volumeToMassConversionId, payload];

		saveFunction(...saveArgs, (response) => {
			if (response && response.status === 200) {
				setToastOpen(true);
				setTimeout(() => {
					props.history.push("/admin/volume-to-mass-conversions");
				}, 1000);
			} else {
				alert("Failed to save volume to mass conversion");
			}
			setIsSaving(false);
		}, (error) => {
			console.error(error);
			alert("Failed to save volume to mass conversion: " + (error.response?.data || error.message));
			setIsSaving(false);
		});
	}

	if (!hasPermission)
		return (
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<NotLoggedInIcon fontSize="large" />
					<Typography variant="subtitle1">
						Sorry, you must {user.isLoggedIn ? "" : "be logged in and "}have volume to mass admin permissions to view this page!
					</Typography>
				</Grid>
			</Grid>
		);

	if (isLoading)
		return <Typography style={{ padding: 20 }}>Loading...</Typography>;

	return (
		<Grid container spacing={3} style={{ padding: 20 }}>
			<Grid item xs={12}>
				<Typography variant="h4" gutterBottom>
					{isCreateMode ? "Create Volume to Mass Conversion" : "Edit Volume to Mass Conversion"}
				</Typography>
			</Grid>
			<Grid item xs={12}>
				<Paper style={{ padding: 20 }}>
					<Grid container spacing={3}>
						{/* Name */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Name:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="name"
									value={item.name}
									onChange={handleChange}
									required
								/>
							</Grid>
						</Grid>
						{/* Density */}
						<Grid item xs={12} container alignItems="center" spacing={2}>
							<Grid item xs={12} md={3} lg={2}>
								<Typography>Density (g/ml):</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="density"
									type="number"
									inputProps={{ step: "0.001" }}
									value={item.density}
									onChange={handleChange}
									required
								/>
							</Grid>
						</Grid>
						{/* Aliases */}
						<Grid item xs={12} container alignItems="flex-start" spacing={2}>
							<Grid item xs={12} md={3} lg={2} style={{ paddingTop: 16 }}>
								<Typography>Aliases:</Typography>
							</Grid>
							<Grid item xs={12} md={9} lg={10}>
								<TextField
									fullWidth
									name="aliases"
									value={item.aliases}
									onChange={handleChange}
									multiline
									rows={1}
									InputProps={{
										inputComponent: AutoResizeTextarea
									}}
									helperText="Line-separated list of alternative names"
								/>
							</Grid>
						</Grid>
		{/* Notes */}
		<Grid item xs={12} container alignItems="flex-start" spacing={2}>
			<Grid item xs={12} md={3} lg={2} style={{ paddingTop: 16 }}>
				<Typography>Notes:</Typography>
			</Grid>
			<Grid item xs={12} md={9} lg={10}>
				<TextField
					fullWidth
					name="notes"
					value={item.notes}
					onChange={handleChange}
					multiline
					rows={1}
					InputProps={{
						inputComponent: AutoResizeTextarea
					}}
				/>
			</Grid>
		</Grid>
						<Grid item xs={12} style={{ marginTop: 16 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								disabled={isSaving}
								style={{ marginRight: 10 }}
							>
								<SaveIcon style={{ marginRight: 10 }} />
								{isSaving ? "Saving..." : "Save"}
							</Button>
							<Button
								variant="outlined"
								component={RouterLink}
								to="/admin/volume-to-mass-conversions"
								disabled={isSaving}
							>
								<CancelIcon style={{ marginRight: 10 }} />
								Cancel
							</Button>
						</Grid>
					</Grid>
				</Paper>
			</Grid>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={toastOpen}
				autoHideDuration={3000}
				onClose={() => setToastOpen(false)}
				style={{ marginTop: 20 }}
			>
				<SnackbarContent
					style={{ backgroundColor: green[600] }}
					message={
						<div style={{ display: "flex", alignItems: "center" }}>
							<ConfirmIcon />
							<span style={{ paddingLeft: 10 }}>Volume to mass conversion saved successfully!</span>
						</div>
					}
				/>
			</Snackbar>
		</Grid>
	);
}
